import React, { useEffect, useRef, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'
import api from 'helpers/api'
import { TABLE } from 'routes'
import Badge from 'components/badge'
import Button from 'components/button'
import ActionDialog from 'components/dialog/action'
import { AlignLeft, Copy, Dashboard, Delete, Lock, Options, Pencil } from 'components/icons'
import Dialog from 'components/dialog'
import Input, { input } from 'components/input'
import Checkbox from 'components/checkbox'
import { BLUE_BUTTON } from 'components/save-buttons'
import Menu from 'components/menu'
import { MenuItem, MenuList } from 'components/menu/items'
import Spinner from 'components/spinner'
import Tooltip from 'components/tooltip'
import { useApplicationStore } from 'hooks/application'
import useProject from 'hooks/useProject'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { CustomViewVersionSource, IContextMenuState, ICustomViewVersion, ITableViewWithColumns } from 'types'
import Callout from './callout'
import PanelHeader from './panel-header'
import { pictureShownVersion } from './thumbnail'

type VersionAction = 'duplicate' | 'delete'

const MORE_MENU_WIDTH = 200

const relativeTime = (createdAt: string) => DateTime.fromISO(createdAt).toRelative() ?? createdAt

// How each kind of version was made, after who made it: "2 days ago · Ann via MortaAI".
const VIA: Record<CustomViewVersionSource, string> = {
  mortaai: 'MortaAI',
  api: 'the API',
  copy: '',
  restore: '',
  view: ''
}

/** Who made a version, as its line puts it: "Ann", or what happened to it ("Copied", "Restored by Ann"). */
function versionWho(version: ICustomViewVersion): string {
  const name = version.createdBy?.name
  if (version.source === 'copy') return 'Copied'
  if (version.source === 'restore') return name ? `Restored by ${name}` : 'Restored'
  // The first version of a Custom View made from a built-in view's settings, such as a Form.
  if (version.source === 'view') return name ? `Made from the built-in view by ${name}` : 'Made from the built-in view'
  return name ?? ''
}

/** When, by whom and how a version was made: one small line under its title and description. */
function VersionOrigin({ version }: { version: ICustomViewVersion }) {
  const who = versionWho(version)
  const via = VIA[version.source] ? `${who ? ' ' : ''}via ${VIA[version.source]}` : ''
  return (
    <span className="truncate text-[11px] leading-4 text-muted" data-testid="custom-view-version-origin">
      <Tooltip content={DateTime.fromISO(version.createdAt).toLocaleString(DateTime.DATETIME_MED)}>
        <time dateTime={version.createdAt}>{relativeTime(version.createdAt)}</time>
      </Tooltip>
      {(who || via) && ` · ${who}${via}`}
    </span>
  )
}

/** The version a view shows its owners in the page: the newest, which may be the published one. */
const newestShownId = (view: {
  customViewLatest?: ICustomViewVersion | null
  customView?: { version?: string } | null
}) => view.customViewLatest?.id ?? view.customView?.version

/** The view, as an owner gets it, says which page is published and which is newest now. */
function useReloadPages() {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const viewId = spreadsheetData.viewDetails.publicId
  return async () => {
    const response = await api.getTableView(viewId)
    setSpreadsheetData({ type: 'SET_CUSTOM_VIEW', viewId, customView: response.data.customView ?? null })
    setSpreadsheetData({ type: 'SET_CUSTOM_VIEW_LATEST', viewId, latest: response.data.customViewLatest ?? null })
  }
}

/** Publishes a version as the page everyone sees, from the bar over the newest or over a previewed version. */
export function usePublishVersion() {
  const { spreadsheetData } = useDataContext()
  const { setSnackbarMessage } = useApplicationStore()
  const reloadPages = useReloadPages()
  const viewId = spreadsheetData.viewDetails.publicId
  return async (version: ICustomViewVersion) => {
    await api.publishCustomViewVersion(viewId, version.id)
    await reloadPages()
    setSnackbarMessage({ status: 'success', message: 'Published: everyone who opens the view sees this version' })
  }
}

/**
 * Over an older version previewed from Versions, in place of the bar over the newest: which version it is, Back to
 * latest and Publish this version. The same quiet bar as the newest's (publish-banner). The published version has
 * none: it's the live page, with nothing to publish. Publishing an older version restores it, after a confirmation:
 * it's copied to the top as the newest version and published, and the versions after it are kept as history or
 * deleted.
 */
export function VersionBanner({ version, onExit }: { version: ICustomViewVersion; onExit: () => void }) {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const [confirming, setConfirming] = useState(false)
  const [deleteNewer, setDeleteNewer] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const viewId = spreadsheetData.viewDetails.publicId

  const publish = () => {
    if (publishing) return
    setPublishing(true)
    api
      .restoreCustomViewVersion(viewId, version.id, deleteNewer)
      .then(({ data: restored }) => {
        // The restored copy is the published page and the newest version, so nothing unpublished is on top. Taken
        // from the response, so the page can't fall back to what it was before.
        const customView = {
          html: restored.html,
          runtimeVersion: 1 as const,
          updatedAt: restored.publishedAt ?? restored.createdAt,
          version: restored.id
        }
        setSpreadsheetData({ type: 'SET_CUSTOM_VIEW', viewId, customView })
        setSpreadsheetData({ type: 'SET_CUSTOM_VIEW_LATEST', viewId, latest: null })
        setSnackbarMessage({ status: 'success', message: 'Published: everyone who opens the view sees this version' })
        // It's the newest version now, which the page shows without a bar.
        onExit()
      })
      .catch(displayErrorMessage)
      .finally(() => setPublishing(false))
  }

  const dialog = confirming && (
    <Dialog
      open
      onOpenChange={(open) => !open && setConfirming(false)}
      title="Publish this version?"
      description="It becomes the page everyone sees, and the newest version, which you and MortaAI carry on from."
      width="small"
      footer={
        <>
          <Button internalType="outline" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
          <Button internalType="outline" className={BLUE_BUTTON} isLoading={publishing} onClick={publish}>
            Publish
          </Button>
        </>
      }
    >
      {/* The box beside the first line of its two. */}
      <Checkbox className="my-0 items-start" checked={deleteNewer} onChange={() => setDeleteNewer(!deleteNewer)}>
        <span className="flex flex-col gap-0.5">
          <span className="text-[13px] leading-4 text-body">Delete the versions made after it</span>
          <span className="text-xs text-muted">Otherwise they stay below it in Versions, as history.</span>
        </span>
      </Checkbox>
    </Dialog>
  )

  return (
    <div
      role="status"
      className="@container flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 min-h-[53px] px-4 py-2 border-b border-divider bg-surface-sunken shrink-0"
      data-testid="custom-view-version-banner"
    >
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 min-w-0">
        <span className="text-sm font-semibold text-body whitespace-nowrap">Preview</span>
        {/* Short, so it sits on one line beside the buttons even with the panel open; the lock says the rest. */}
        <Tooltip content="Rows can't be changed in a preview">
          <span className="flex items-center gap-1 text-xs text-muted whitespace-nowrap">
            <Lock className="w-3 h-3" />
            Version · {relativeTime(version.createdAt)}
          </span>
        </Tooltip>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Button internalType="outline" onClick={onExit} disabled={publishing}>
          <span>
            Back<span className="@max-[520px]:hidden"> to latest</span>
          </span>
        </Button>
        <Button
          internalType="outline"
          // Morta's blue (deep enough to carry white text), as on the bar over the newest version.
          className="bg-blue-strong text-on-solid border-transparent hover:bg-blue-strong hover:brightness-95"
          onClick={() => {
            setDeleteNewer(false)
            setConfirming(true)
          }}
          disabled={publishing}
          aria-label="Publish this version"
        >
          <span>
            Publish<span className="@max-[520px]:hidden"> this version</span>
          </span>
        </Button>
      </div>
      {dialog}
    </div>
  )
}

/**
 * A version's title. Double-click it (or Rename in its menu) to change it in place: Enter or leaving the field saves,
 * Escape puts it back.
 */
function VersionTitle({
  version,
  editing,
  onEdit,
  onSave,
  onClick
}: {
  version: ICustomViewVersion
  editing: boolean
  onEdit: (editing: boolean) => void
  onSave: (title: string) => void
  /** A click on the title is a click on its card. */
  onClick: () => void
}) {
  const [text, setText] = useState(version.title ?? '')
  useEffect(() => {
    if (editing) setText(version.title ?? '')
  }, [editing])

  if (editing) {
    const done = () => {
      onEdit(false)
      if (text.trim() !== (version.title ?? '')) onSave(text.trim())
    }
    return (
      <Input
        aria-label="Title"
        // Opened by a double-click or Rename, to type in straight away.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        maxLength={80}
        placeholder="Untitled version"
        className="relative z-10 h-6 -my-px px-1.5 -mx-1.5 text-[13px] font-medium"
        value={text}
        onChange={(event) => setText(event.target.value)}
        onBlur={done}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur()
          if (event.key === 'Escape') {
            event.stopPropagation()
            setText(version.title ?? '')
            onEdit(false)
          }
        }}
      />
    )
  }
  return (
    <Tooltip content="Double-click to rename">
      <span
        className={`line-clamp-2 text-[13px] font-medium leading-5 ${version.title ? 'text-body' : 'text-muted'}`}
        onClick={onClick}
        onDoubleClick={() => onEdit(true)}
      >
        {version.title || 'Untitled version'}
      </span>
    </Tooltip>
  )
}

/** A version's picture: the one just taken (``taken``, a data: URL), or the one it has, fetched. */
function Thumbnail({ viewId, version, taken }: { viewId: string; version: ICustomViewVersion; taken?: string }) {
  const [fetched, setFetched] = useState<string | null>(null)
  useEffect(() => {
    if (taken || !version.hasThumbnail) return
    let cancelled = false
    let url: string | null = null
    api
      .getCustomViewVersionThumbnail(viewId, version.id)
      .then((image) => {
        if (cancelled) return
        url = URL.createObjectURL(image)
        setFetched(url)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [viewId, version.id, version.hasThumbnail, taken])
  const picture = taken ?? fetched
  return (
    <div className="flex items-center justify-center shrink-0 w-[88px] h-[55px] overflow-hidden rounded-md border border-divider bg-surface-sunken">
      {picture ? (
        <img src={picture} alt="" className="w-full h-full object-cover object-top" />
      ) : (
        <Dashboard className="w-5 h-5 text-muted" />
      )}
    </div>
  )
}

/**
 * The Custom View's Versions panel, for table owners: every saved version of the page, newest first, each with a
 * small picture, MortaAI's line about it and the message that asked for it. Preview shows a version in the page,
 * read-only, under a bar that publishes it; Duplicate view makes a new view from it; Delete removes it (not the
 * published one). A version shown in the page without a picture has one taken.
 */
export default function VersionsPanel({
  previewing,
  onPreview
}: {
  /** The version previewed in the page, if not the newest. */
  previewing: ICustomViewVersion | null
  /** An older version, with its HTML, to show in the page; null to show the newest. */
  onPreview: (version: ICustomViewVersion | null) => void
}) {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const { project } = useProject()
  const navigate = useNavigate()
  const reloadPages = useReloadPages()
  const view = spreadsheetData.viewDetails
  const viewId = view.publicId
  const [versions, setVersions] = useState<ICustomViewVersion[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [busy, setBusy] = useState<{ id: string; action: VersionAction } | null>(null)
  const [loadingId, setLoadingId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<ICustomViewVersion | null>(null)
  // A version's ⋯ menu (Duplicate view, Delete version): one at a time.
  const [menu, setMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [menuFor, setMenuFor] = useState<ICustomViewVersion | null>(null)
  // The version whose title and description are being edited, and the text so far.
  const [describing, setDescribing] = useState<ICustomViewVersion | null>(null)
  const [description, setDescription] = useState('')
  // The version whose title is being changed in place.
  const [renaming, setRenaming] = useState<string | null>(null)
  const [savingDescription, setSavingDescription] = useState(false)
  // Only the last version clicked is shown, however its HTML and earlier ones' arrive.
  const lastClick = useRef(0)
  // Versions whose picture is being taken, so each is taken once; and those taken, shown without fetching them.
  const picturing = useRef(new Set<string>())
  const [taken, setTaken] = useState<Record<string, string>>({})
  // The list follows the view's newest and published versions, so a save or publish anywhere shows here.
  const latestId = view.customViewLatest?.id
  const publishedId = view.customView?.version
  const shownId = previewing?.id ?? newestShownId(view)

  useEffect(() => {
    let cancelled = false
    setFailed(false)
    api
      .getCustomViewVersions(viewId)
      .then((response) => {
        if (!cancelled) setVersions(response.data)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [viewId, latestId, publishedId])

  // Closing the panel (or switching it to another mode) shows the newest version again.
  useEffect(() => {
    const clicks = lastClick
    return () => {
      clicks.current += 1
      onPreview(null)
    }
  }, [])

  // The version in the page gets a picture, if it has none: a nice-to-have, so a failure is left quiet.
  useEffect(() => {
    const version = versions?.find((candidate) => candidate.id === shownId)
    if (!version || version.hasThumbnail || picturing.current.has(version.id)) return
    picturing.current.add(version.id)
    pictureShownVersion(version.id)
      .then(async (thumbnail) => {
        if (!thumbnail) return
        await api.updateCustomViewVersion(viewId, version.id, { thumbnail })
        setTaken((pictures) => ({ ...pictures, [version.id]: thumbnail }))
        setVersions(
          (list) => list?.map((item) => (item.id === version.id ? { ...item, hasThumbnail: true } : item)) ?? null
        )
      })
      .catch(() => undefined)
  }, [shownId, versions, viewId])

  // The version already in the page stays; previewing the newest takes the page back to it.
  const preview = (version: ICustomViewVersion) => {
    if (version.id === previewing?.id || (!previewing && version.id === newestShownId(view))) return
    const click = ++lastClick.current
    if (version.id === newestShownId(view)) {
      setLoadingId(null)
      onPreview(null)
      return
    }
    setLoadingId(version.id)
    api
      .getCustomViewVersion(viewId, version.id)
      .then((response) => {
        if (click === lastClick.current) onPreview({ ...version, html: response.data.html ?? '' })
      })
      .catch((error) => {
        if (click === lastClick.current) displayErrorMessage(error)
      })
      .finally(() => {
        if (click === lastClick.current) setLoadingId(null)
      })
  }

  const duplicate = (version: ICustomViewVersion) => {
    if (busy) return
    setBusy({ id: version.id, action: 'duplicate' })
    api
      .duplicateCustomViewVersion(spreadsheetData.tableDetails.publicId, viewId, version.id)
      .then((response) => {
        const created = response.data as ITableViewWithColumns
        setSpreadsheetData({ type: 'ADD_VIEW', view: created, inProcess: false })
        setSnackbarMessage({ status: 'success', message: `Duplicated as ${created.name}` })
        navigate(
          generatePath(TABLE, {
            projectId: project.publicId,
            tableId: spreadsheetData.tableDetails.publicId,
            viewId: created.publicId
          })
        )
      })
      .catch(displayErrorMessage)
      .finally(() => setBusy(null))
  }

  // A failure reaches the confirmation, which says so and stays open to try again.
  const remove = (version: ICustomViewVersion) => {
    setBusy({ id: version.id, action: 'delete' })
    return api
      .deleteCustomViewVersion(viewId, version.id)
      .then(() => {
        if (previewing?.id === version.id) onPreview(null)
        setVersions((list) => list?.filter((item) => item.id !== version.id) ?? null)
        setSnackbarMessage({ status: 'success', message: 'Version deleted' })
        // Deleting the newest makes the one before it what the page shows. The version is gone either way, so a
        // failed reload is reported here rather than keeping the confirmation open to delete it again.
        if (version.isLatest) reloadPages().catch(displayErrorMessage)
      })
      .finally(() => setBusy(null))
  }

  const openMenu = (version: ICustomViewVersion, event: React.MouseEvent<HTMLButtonElement>) => {
    if (menu.open && menuFor?.id === version.id) {
      setMenu(INITIAL_CONTEXT_MENU_STATE)
      return
    }
    const box = event.currentTarget.getBoundingClientRect()
    setMenuFor(version)
    // Right-aligned with the button, which sits at the card's right.
    setMenu({
      open: true,
      top: `${box.bottom + 4}px`,
      left: `${box.right - MORE_MENU_WIDTH}px`,
      right: 'auto',
      bottom: 'auto'
    })
  }

  const fromMenu = (action: (version: ICustomViewVersion) => void) => () => {
    setMenu(INITIAL_CONTEXT_MENU_STATE)
    if (menuFor) action(menuFor)
  }

  // An owner's own title or description for a version, in place of MortaAI's.
  const saveText = (version: ICustomViewVersion, text: { title?: string; summary?: string }) =>
    api.updateCustomViewVersion(viewId, version.id, text).then((response) => {
      const saved = { title: response.data.title ?? null, summary: response.data.summary ?? null }
      setVersions((list) => list?.map((item) => (item.id === version.id ? { ...item, ...saved } : item)) ?? null)
    })

  const saveDescription = () => {
    if (!describing || savingDescription) return
    setSavingDescription(true)
    saveText(describing, { summary: description.trim() })
      .then(() => setDescribing(null))
      .catch(displayErrorMessage)
      .finally(() => setSavingDescription(false))
  }

  // MortaAI's description of the version's changes (or the owner's own). Versions saved before it wrote them show
  // the message that asked for them instead.
  const describe = (version: ICustomViewVersion) => {
    const summary = version.summary || (version.prompt ? `“${version.prompt}”` : null)
    return summary ? <p className="mt-0.5 line-clamp-2 text-xs leading-[18px] text-muted">{summary}</p> : null
  }

  const renderList = () => {
    if (failed) return <Callout>The versions could not be loaded. Close the panel and try again.</Callout>
    if (versions === null) {
      return (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      )
    }
    if (versions.length === 0) {
      return <p className="text-sm text-secondary">No versions yet. Each page MortaAI saves adds one.</p>
    }
    return (
      <ul className="flex flex-col gap-2" aria-label="Versions">
        {versions.map((version) => {
          const onPage = version.id === shownId
          const working = (action: VersionAction) => busy?.id === version.id && busy.action === action
          return (
            <li
              key={version.id}
              className={`group relative flex gap-3 rounded-lg border p-3 transition-colors ${
                onPage ? 'border-blue bg-blue/5' : 'border-divider hover:bg-surface-sunken'
              }`}
              data-testid={`custom-view-version-${version.id}`}
              aria-busy={loadingId === version.id || working('duplicate')}
            >
              {/* The whole card previews the version; the title and the menu sit above it. */}
              <Button
                internalType="transparent"
                className="absolute inset-0 z-0 h-auto w-auto rounded-lg p-0 hover:bg-transparent"
                onClick={() => preview(version)}
                aria-pressed={onPage}
                aria-label={onPage ? 'Previewing' : 'Preview'}
              />
              <Thumbnail viewId={viewId} version={version} taken={taken[version.id]} />
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex min-w-0 items-start gap-2">
                  <span className="relative z-10 flex min-w-0 flex-1">
                    <VersionTitle
                      version={version}
                      editing={renaming === version.id}
                      onEdit={(editing) => setRenaming(editing ? version.id : null)}
                      onSave={(title) => saveText(version, { title }).catch(displayErrorMessage)}
                      onClick={() => preview(version)}
                    />
                  </span>
                  {(onPage || version.isPublished) && (
                    <span className="flex shrink-0 gap-1 pt-px">
                      {/* The published page isn't a preview: it's what everyone sees. Its outline says it's showing. */}
                      {onPage && !version.isPublished && <Badge>Previewing</Badge>}
                      {version.isPublished && <Badge tone="blue">Published</Badge>}
                    </span>
                  )}
                </div>
                {describe(version)}
                <div className="mt-auto flex items-center justify-between gap-2 pt-1.5">
                  <VersionOrigin version={version} />
                  <Tooltip content="More actions">
                    <span className="relative z-10 -my-1 -mr-1.5">
                      <Button
                        internalType="transparent"
                        className="h-6 w-6 px-0 text-muted hover:text-body"
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => openMenu(version, event)}
                        aria-label="More actions"
                        aria-haspopup="menu"
                        aria-expanded={menu.open && menuFor?.id === version.id}
                      >
                        <Options className="w-4 h-4" />
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="custom-view-versions-panel">
      <PanelHeader title="Versions" />
      <div className="flex flex-col flex-1 min-h-0 gap-3 p-4 overflow-y-auto">{renderList()}</div>
      <Menu id="custom-view-version-menu" menuState={menu} setMenuState={setMenu} width={MORE_MENU_WIDTH}>
        <MenuList>
          <MenuItem icon={<Pencil />} onClick={fromMenu((version) => setRenaming(version.id))}>
            Rename
          </MenuItem>
          <MenuItem
            icon={<AlignLeft />}
            onClick={fromMenu((version) => {
              setDescription(version.summary ?? '')
              setDescribing(version)
            })}
          >
            Edit description
          </MenuItem>
          <MenuItem icon={<Copy />} onClick={fromMenu(duplicate)} disabled={!!busy}>
            Duplicate view
          </MenuItem>
          <MenuItem
            icon={<Delete />}
            variant="destructive"
            onClick={fromMenu(setDeleting)}
            disabled={!!busy || !!menuFor?.isPublished}
            disabledReason={
              menuFor?.isPublished
                ? "The published version can't be deleted. Publish another version first."
                : undefined
            }
          >
            Delete version
          </MenuItem>
        </MenuList>
      </Menu>
      {describing && (
        <Dialog
          open
          onOpenChange={(open) => !open && setDescribing(null)}
          title="Edit description"
          description="What changed in this version, shown under its title in Versions in place of what MortaAI wrote."
          width="small"
          footer={
            <>
              <Button internalType="outline" onClick={() => setDescribing(null)}>
                Cancel
              </Button>
              <Button
                internalType="outline"
                className={BLUE_BUTTON}
                isLoading={savingDescription}
                onClick={saveDescription}
              >
                Save
              </Button>
            </>
          }
        >
          <form
            onSubmit={(event) => {
              event.preventDefault()
              saveDescription()
            }}
          >
            <textarea
              aria-label="Description"
              data-slot="textarea"
              className={input({ class: 'h-auto min-h-[76px] py-2 resize-none' })}
              rows={3}
              maxLength={300}
              placeholder="What changed in this version"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </form>
        </Dialog>
      )}
      {deleting && (
        <ActionDialog
          title="Delete this version?"
          description={`The version from ${relativeTime(deleting.createdAt)} is deleted for good. This can’t be undone.${
            deleting.isLatest ? ' The page goes back to the version before it.' : ''
          }`}
          confirmLabel="Delete version"
          danger
          onClose={() => setDeleting(null)}
          onConfirm={() =>
            remove(deleting).catch((error) => {
              displayErrorMessage(error)
              throw error
            })
          }
        />
      )}
    </div>
  )
}
